import React from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Mail, Send, CheckCircle } from 'lucide-react';

const Newsletter = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const onSubmit = (data) => {
    // Simulate form submission
    console.log('Newsletter signup:', data);
    setIsSubmitted(true);
    reset();
    
    // Reset success message after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <section id="newsletter" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-center mb-6">
            <Mail className="h-10 w-10 text-primary-500 mr-4" />
            <h2 className="text-4xl font-bold text-gray-800">
              Join Our Green Thumb Community
            </h2>
          </div>
          
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Get weekly plant care tips, seasonal guides, and exclusive content delivered straight to your inbox.
          </p>

          {/* Success Message */}
          {isSubmitted && (
            <motion.div
              className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 flex items-center justify-center space-x-3"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <CheckCircle className="h-6 w-6 text-green-500" />
              <span className="text-green-800 font-medium">
                Thank you for subscribing!
              </span>
            </motion.div>
          )}

          {/* Newsletter Form */}
          <motion.form
            onSubmit={handleSubmit(onSubmit)}
            className="max-w-lg mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="flex-1 w-full">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className={`w-full px-5 py-3 rounded-full bg-white border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all duration-300 shadow-sm`}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-2 text-left pl-4">
                    {errors.email.message}
                  </p>
                )}
              </div>
              
              <motion.button
                type="submit"
                className="w-full sm:w-auto group bg-gradient-to-r from-primary-500 to-green-500 hover:from-primary-600 hover:to-green-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105"
                whileTap={{ scale: 0.95 }}
              >
                <span>Subscribe</span>
                <Send className="h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>
            </div>
          </motion.form>

          <p className="text-gray-500 text-sm mt-4">
            No spam, unsubscribe at any time.
          </p>

          {/* Benefits */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, staggerChildren: 0.2 }}
            viewport={{ once: true }}
          >
            {[ 
              { icon: Mail, title: 'Weekly Tips', text: 'Expert plant care advice.' },
              { icon: CheckCircle, title: 'Exclusive Content', text: 'Subscriber-only guides.' },
              { icon: Send, title: 'Seasonal Guides', text: 'Timely advice for plant care.' }
            ].map((item, index) => (
              <motion.div 
                key={index} 
                className="text-center p-6 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" 
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              >
                <div className="bg-gradient-to-r from-primary-500 to-green-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-md">
                  <item.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
